import {
  AUDIT_REDACTED,
  CATALOG_STATUSES,
  PRICING_TEXT_VARIABLES,
  RETENTION_MAX_YEARS,
  RETENTION_MIN_YEARS,
  TenantSlugSchema,
  confirmationPhraseFor,
  effectiveFeature,
  isSensitiveField,
  pricingTextProblem,
  slugify,
  type AdminIntegration,
  type AdminInstall,
  type CatalogEntry,
  type CatalogStatus,
  type CreateCatalogEntry,
  type CreatePlanRequest,
  type CreateTenantRequest,
  type DeadLetterJob,
  type DomainAppKind,
  type DomainStatus,
  type ErrorGroup,
  type FeatureKey,
  type FeedbackCategory,
  type FeedbackStatus,
  type FeedbackVisibility,
  type HealthProbe,
  type InstallHealth,
  type InstallHealthSummary,
  type IntegrationCall,
  type IntegrationCallStatus,
  type IntegrationCategory,
  type IntegrationHealthCheck,
  type InvitePlatformUserRequest,
  type ModerateFeedbackRequest,
  type PageInfo,
  type PendingPromotion,
  type PendingPromotionRow,
  type Plan,
  type PlatformAuditRow,
  type PlatformFeedbackItem,
  type PlatformHealth,
  type PlatformRole,
  type PlatformSettings,
  type PlatformSettingsPatch,
  type PlatformTenantBranding,
  type PlatformUserDetail,
  type PlatformUserSummary,
  type PricingPresentationTexts,
  type PromotionStatus,
  type PutCredentialsRequest,
  type QueueSummary,
  type RequestRange,
  type RequestSummary,
  type PlatformUploadUrlRequest,
  type SaveBrandingRequest,
  type TenantDetail,
  type TenantDomainDetail,
  type TenantFeature,
  type TenantHealthRow,
  type TenantInstall,
  type TenantStatus,
  type TenantSummary,
  type TenantUsage,
  type TenantUsageRow,
  type TenantUser,
  type UpdateCatalogEntry,
  type UpdateTenantRequest,
  type UserAccountStatus,
  type UserPopulation,
  type UserSessionRow,
} from '@platform/contracts';

/**
 * The super admin's data seam (MASTER_PLAN M12.7, M3.8, Docs/API.md §2.13).
 *
 * Every method is one endpoint from §2.13, named and shaped the way the API spells it.
 * **The types are `@platform/contracts`' own** - the same schemas the API validates its
 * responses against - rather than a second set declared here. That is the substance of
 * M12.7: the console and the server cannot disagree about a field name, because there is
 * only one declaration of it. Two implementations satisfy the interface: `platform.api.ts`
 * over `@platform/api-client`, and `platform.mock.ts` in memory for tests and
 * `VITE_USE_MOCKS=true`. Which one a screen gets is decided in
 * `platform-client-context.tsx` and nowhere else.
 *
 * Screens never fetch: the route hands them data and callbacks, which is what makes them
 * testable without a server (rules §17.1, §17.8).
 *
 * Permissions are the server's, not this file's (rules §17.2, §19.11). A disabled button
 * here is a courtesy and never the enforcement point.
 *
 * No PHI crosses this seam: tenants, plans, features, domains, branding, catalogue
 * entries, counts and audit metadata are configuration, not patient data (SECURITY.md
 * §6.1, rules §25).
 */

/* --------------------------------------------------------- contract re-exports */

export type {
  /** One tenant's install in full: catalog entry, hint, health, settings (M16.2). */
  AdminIntegration,
  AdminInstall,
  CatalogEntry,
  CatalogStatus,
  CreateCatalogEntry,
  CreatePlanRequest,
  CreateTenantRequest,
  DeadLetterJob,
  DomainAppKind,
  DomainStatus,
  ErrorGroup,
  FeatureKey,
  FeedbackCategory,
  FeedbackStatus,
  FeedbackVisibility,
  HealthProbe,
  InstallHealth,
  InstallHealthSummary,
  IntegrationCall,
  IntegrationCallStatus,
  IntegrationCategory,
  IntegrationHealthCheck,
  InvitePlatformUserRequest,
  ModerateFeedbackRequest,
  PageInfo,
  PendingPromotion,
  /** One row of `GET /platform/promotions`: the request, who asked and who it is for. */
  PendingPromotionRow,
  Plan,
  PlatformAuditRow,
  PlatformFeedbackItem,
  PlatformHealth,
  PlatformRole,
  PlatformSettings,
  PlatformSettingsPatch,
  PlatformTenantBranding,
  PlatformUploadUrlRequest,
  PlatformUserDetail,
  PlatformUserSummary,
  PricingPresentationTexts,
  PromotionStatus,
  PutCredentialsRequest,
  /** One pg-boss queue as `GET /platform/logs/jobs` returns it (ADR-006). */
  QueueSummary,
  RequestRange,
  RequestSummary,
  SaveBrandingRequest,
  TenantDetail,
  TenantDomainDetail,
  TenantFeature,
  TenantHealthRow,
  TenantInstall,
  TenantStatus,
  TenantSummary,
  /** One clinic's counters over a range. Counts only - never rows, never PHI. */
  TenantUsage,
  TenantUsageRow,
  TenantUser,
  UpdateCatalogEntry,
  UpdateTenantRequest,
  UserAccountStatus,
  UserPopulation,
  UserSessionRow,
};

export {
  AUDIT_REDACTED,
  PRICING_TEXT_VARIABLES,
  RETENTION_MAX_YEARS,
  RETENTION_MIN_YEARS,
  confirmationPhraseFor,
  effectiveFeature,
  isSensitiveField,
  slugify,
};

export {
  PLATFORM_ROLE_LABELS,
  POPULATION_LABELS,
  REQUEST_RANGE_LABELS,
  SLUG_PATTERN,
} from '@platform/contracts';

/* ------------------------------------------------------------- UI query shapes */

/**
 * The screens' filter state, which is not the wire query: a select needs a value for
 * "no filter" and `'all'` is it. The clients drop the sentinel, so a query string never
 * carries `status=all` (API.md §1.7).
 */
export interface TenantListQuery {
  /** Free text over name and slug. */
  q?: string;
  status?: TenantStatus | 'all';
  planId?: string | 'all';
  limit?: number;
  cursor?: string | null;
}

export interface PlatformUserListQuery {
  q?: string;
  population?: UserPopulation | 'all';
  tenantId?: string | 'all';
  status?: UserAccountStatus | 'all';
  mfa?: 'all' | 'enrolled' | 'missing';
  limit?: number;
  cursor?: string | null;
}

export interface AuditQuery {
  tenantId?: string | 'all';
  actorId?: string;
  /** Free text matched against the action name. */
  action?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string | null;
}

export interface IntegrationCallFilters {
  tenantId?: string | 'all';
  integrationKey?: string | 'all';
  status?: IntegrationCallStatus | 'all';
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string | null;
}

/**
 * `{ health, install }`: what `PUT …/credentials` and `POST …/test` answer
 * (`TestConnectionResponseSchema`, unwrapped). The same shape the clinic admin gets.
 */
export interface InstallProbeResult {
  health: HealthProbe;
  install: AdminInstall;
}

/** The query `GET /platform/tenants/:id/installs/:key/calls` takes (`IntegrationCallQuerySchema`). */
export interface InstallCallQuery {
  status?: IntegrationCallStatus;
  operation?: string;
  limit?: number;
  cursor?: string | null;
}

export interface FeedbackQuery {
  /** `'platform'` selects the items with no tenant, the way the board's select spells it. */
  tenantId?: string | 'all' | 'platform';
  category?: FeedbackCategory | 'all';
  status?: FeedbackStatus | 'all';
  visibility?: FeedbackVisibility | 'all';
  limit?: number;
  cursor?: string | null;
}

/** `{ data, page }` exactly as API.md §1.7 returns it. */
export interface Page<Row> {
  data: Row[];
  page: PageInfo;
}

/* ------------------------------------------------------------------ helpers */

/**
 * Validates a slug the way the database does, and says why in the clinic's words rather
 * than by showing the regular expression.
 *
 * The pattern itself is `TenantSlugSchema` in `@platform/contracts` - the same expression
 * `ck_tenants_slug` enforces - so a slug this accepts cannot fail the insert.
 */
export function slugProblem(slug: string): string | null {
  if (slug === '') return 'A slug is required.';
  if (slug.length < 3) return 'Use at least 3 characters.';
  if (slug.length > 40) return 'Use at most 40 characters.';
  return TenantSlugSchema.safeParse(slug).success
    ? null
    : 'Use lower-case letters, numbers and hyphens only.';
}

export interface AuditDiffEntry {
  key: string;
  before: string;
  after: string;
  /** True when the value was withheld rather than absent. */
  redacted: boolean;
}

/**
 * The fields that changed, as strings ready to render. Keys present in either side are
 * listed once, in a stable order, so the same row always reads the same way.
 *
 * The server redacts on the way out (`redactAuditPayload`); `isSensitiveField` is the
 * same predicate applied again here, because an audit row is written by a hundred
 * different call sites and the one that forgets must not be able to paint a token onto a
 * support engineer's screen (SECURITY.md §7.4, rules §32.5-7).
 */
export function auditDiff(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): AuditDiffEntry[] {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].sort();

  return keys.map((key) => {
    const redacted = isSensitiveField(key);
    const show = (side: Record<string, unknown> | null): string => {
      if (side === null || !(key in side)) return '—';
      if (redacted) return AUDIT_REDACTED;
      const value = side[key];
      return value === null ? 'null' : typeof value === 'string' ? value : JSON.stringify(value);
    };
    return { key, before: show(before), after: show(after), redacted };
  });
}

export const PRICING_TEXT_LABELS: Record<keyof PricingPresentationTexts, string> = {
  selfPay: 'Self-pay price',
  copayEstimate: 'Copay estimate',
  pendingReview: 'Pending review',
};

/**
 * Validates a settings patch the way `PATCH /platform/settings` will, keyed by field so
 * the form can put each message under its own control (rules §48.3).
 *
 * The rules come from `PlatformSettingsPatchSchema` and `pricingTextProblem()` in
 * `@platform/contracts`; what is added here is the wording and the field keys, which are
 * a presentation decision the API has no opinion about.
 */
export function settingsProblems(patch: PlatformSettingsPatch): Record<string, string> {
  const problems: Record<string, string> = {};

  if (patch.retentionYears !== undefined) {
    const years = patch.retentionYears;
    if (!Number.isInteger(years)) {
      problems['retentionYears'] = 'Use a whole number of years.';
    } else if (years < RETENTION_MIN_YEARS) {
      problems['retentionYears'] =
        `Retention cannot go below ${String(RETENTION_MIN_YEARS)} years (ADR-021).`;
    } else if (years > RETENTION_MAX_YEARS) {
      problems['retentionYears'] = `Use at most ${String(RETENTION_MAX_YEARS)} years.`;
    }
  }

  if (patch.pricingTexts !== undefined) {
    for (const key of Object.keys(PRICING_TEXT_VARIABLES) as (keyof PricingPresentationTexts)[]) {
      const text = patch.pricingTexts[key];
      const field = `pricingTexts.${key}`;
      if (text.trim() === '') {
        problems[field] = 'This text is shown to patients and cannot be empty.';
        continue;
      }
      if (text.length > 300) problems[field] = 'Use at most 300 characters.';
    }
    const unknown = pricingTextProblem(patch.pricingTexts);
    if (unknown !== null) {
      const allowed = PRICING_TEXT_VARIABLES[unknown.field];
      problems[`pricingTexts.${unknown.field}`] =
        `${unknown.variable} is not available here. Use ${allowed.join(', ')}.`;
    }
  }

  if (patch.maintenanceMode?.enabled === true && patch.maintenanceMode.message.trim().length < 10) {
    problems['maintenanceMode.message'] =
      'Say what is happening and when it ends. At least 10 characters.';
  }

  for (const key of ['sentryUrl', 'monitorUrl'] as const) {
    const value = patch[key];
    if (value === undefined || value === null || value === '') continue;
    if (!/^https:\/\/\S+$/.test(value)) problems[key] = 'Use a full https:// link.';
  }

  return problems;
}

/* ----------------------------------------------------------------- feedback */

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  open: 'Open',
  planned: 'Planned',
  in_progress: 'In progress',
  shipped: 'Shipped',
  declined: 'Declined',
  duplicate: 'Duplicate',
};

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: 'Bug',
  feature: 'Feature request',
  general: 'General',
  security: 'Security',
};

/**
 * Rules §50.10 and §50.11, in one function so the screen, the mock and the test agree:
 * a security report is never public, and nothing becomes public by itself.
 *
 * `visibilityFor()` in `@platform/contracts` is the server's half of the same rule; this
 * one exists to say *why* the control is disabled, which a coercing function cannot.
 */
export function visibilityProblem(
  item: { category: FeedbackCategory },
  next: FeedbackVisibility,
): string | null {
  if (next === 'public' && item.category === 'security') {
    return 'Security reports stay private. Move it out of the security category first.';
  }
  return null;
}

/* ------------------------------------------------------------------- client */

export interface PlatformClient {
  /* ---------------------------------------------------------------- tenants */

  /** `GET /platform/tenants` (platform.tenant.read). Cursor-paged. */
  listTenants(query?: TenantListQuery): Promise<Page<TenantSummary>>;
  /** `GET /platform/tenants/:id` (platform.tenant.read). */
  getTenant(tenantId: string): Promise<TenantDetail>;
  /**
   * `POST /platform/tenants` (platform.tenant.manage, **step-up**).
   *
   * Answers the tenant *and* the first clinic admin's invitation. `adminEmail` is what
   * asks for one; the field is optional, and a create without it answers `invitation:
   * null`. **As built the endpoint answers `null` either way** - the request schema
   * accepts `adminEmail` but `TenantsController.create` hard-codes the field until M2.7's
   * invite flow is reachable from here. The screen reports what came back rather than
   * what was asked for, so it never claims an invitation nobody sent.
   */
  createTenant(input: CreateTenantRequest): Promise<{
    tenant: TenantSummary;
    invitation: { id: string; email: string; expiresAt: string } | null;
  }>;
  /** `PATCH /platform/tenants/:id` (platform.tenant.manage). No `slug`: see API.md §2.13. */
  updateTenant(tenantId: string, patch: UpdateTenantRequest): Promise<TenantSummary>;
  /** `POST /platform/tenants/:id/suspend` (platform.tenant.manage, **step-up**). */
  setTenantSuspended(
    tenantId: string,
    input: { suspended: boolean; reason: string },
  ): Promise<TenantSummary>;
  /** `POST /platform/tenants/:id/archive` (platform.tenant.manage, **step-up**). */
  archiveTenant(tenantId: string, input: { reason: string }): Promise<TenantSummary>;

  /* ---------------------------------------------------------------- domains */

  /** `GET /platform/tenants/:tenantId/domains` (platform.tenant.read), via DomainsModule. */
  listDomains(tenantId: string): Promise<TenantDomainDetail[]>;
  /**
   * `POST /platform/tenants/:tenantId/domains/:domainId/verify` (tenant.domains.manage).
   * Re-runs the DNS check and activates on success.
   */
  verifyDomain(tenantId: string, domainId: string): Promise<TenantDomainDetail>;
  /**
   * `POST /platform/tenants/:tenantId/domains` (platform.tenant.manage +
   * tenant.domains.manage), answering `201`.
   *
   * The clinic's own rules apply unchanged, and they are the server's: the plan must
   * carry `custom_domains`, a host under the platform domain is refused, and a host
   * another clinic already holds answers `409 DUPLICATE` **without naming that clinic**.
   * The screen renders the refusal verbatim, so it cannot leak what the API withheld.
   */
  addDomain(
    tenantId: string,
    input: { host: string; appKind: DomainAppKind },
  ): Promise<TenantDomainDetail>;
  /**
   * `DELETE /platform/tenants/:tenantId/domains/:domainId` (…, **step-up**), answering
   * `204`.
   *
   * Step-up is the call site's business (`useStepUp().guard`), for the reason the
   * clinic's own delete carries it: removing a hostname takes a live address off the air
   * for every patient who has it bookmarked. The clinic address we assign
   * (`kind = 'platform_subdomain'`) is refused `409 CONFLICT` whoever asks, which is why
   * the screen renders no delete action against one.
   */
  removeDomain(tenantId: string, domainId: string): Promise<void>;

  /* --------------------------------------------------- branding and features */

  /**
   * `PUT /platform/tenants/:id/branding` (platform.tenant.manage). The clinic's own
   * partial (`UpdateBrandingRequest`, M17.1): read-only fields are never sent, and a file
   * id the tenant does not own is a `400` naming the field.
   */
  saveBranding(tenantId: string, input: SaveBrandingRequest): Promise<PlatformTenantBranding>;
  /**
   * A logo or favicon for the tenant (M17.2): `POST /platform/tenants/:tenantId/files/
   * upload-url` → `PUT` the bytes to the ticket's URL → `POST …/files/:id/complete`
   * (platform.tenant.manage). Resolves to the new file id — what `saveBranding` then
   * stores — and a URL to preview it by when one is available (none is, today: the
   * platform surface has no download route, so the form previews the picked file itself).
   */
  uploadTenantFile(
    tenantId: string,
    kind: PlatformUploadKind,
    file: File,
  ): Promise<{ fileId: string; url: string | null }>;
  /** `GET /platform/tenants/:id/features` (platform.tenant.read). Every key, no holes. */
  listTenantFeatures(tenantId: string): Promise<TenantFeature[]>;
  /**
   * `PUT /platform/tenants/:id/features/:featureKey` (platform.features.manage, audited).
   * `null` **deletes** the override row so the plan applies again, which is not the same
   * as `false` ("never, whatever the plan says").
   */
  setTenantFeature(
    tenantId: string,
    featureKey: FeatureKey,
    enabled: boolean | null,
  ): Promise<TenantFeature[]>;

  /* --------------------------------------------------------------- installs */

  /** `GET /platform/tenants/:id/installs` (platform.integrations.enable). */
  listInstalls(tenantId: string): Promise<TenantInstall[]>;
  /** `POST /platform/tenants/:id/installs/:key/enable` (platform.integrations.enable). */
  enableInstall(tenantId: string, integrationKey: string): Promise<TenantInstall>;
  /** `POST /platform/tenants/:id/installs/:key/disable` (…, **step-up**). */
  disableInstall(
    tenantId: string,
    integrationKey: string,
    input: { reason: string },
  ): Promise<TenantInstall>;

  /*
   * One install in full (M16.2, ADR-033). Every route runs as the tenant through the
   * same `InstallsService` the clinic admin's routes use, so the answers are the clinic's
   * own shapes. Reads take `platform.integrations.enable`; writes and the probe take
   * `platform.integrations.manage` (platform_admin only) and are refused while
   * impersonating. **No route returns a credential**: `credHint` is the only trace.
   */

  /** `GET /platform/tenants/:id/installs/:key`. `install` is null when not enabled. */
  getInstallDetail(tenantId: string, integrationKey: string): Promise<AdminIntegration>;
  /**
   * `PUT /platform/tenants/:id/installs/:key/credentials` (…manage, **step-up**). Stores
   * the bundle encrypted for the tenant and probes it in the same request. Step-up is the
   * call site's business (`useStepUp().guard`).
   */
  putInstallCredentials(
    tenantId: string,
    integrationKey: string,
    body: PutCredentialsRequest,
  ): Promise<InstallProbeResult>;
  /** `DELETE /platform/tenants/:id/installs/:key/credentials` (…manage, **step-up**). */
  deleteInstallCredentials(tenantId: string, integrationKey: string): Promise<AdminIntegration>;
  /** `PATCH /platform/tenants/:id/installs/:key/settings` (…manage). Non-secret only. */
  patchInstallSettings(
    tenantId: string,
    integrationKey: string,
    settings: Record<string, unknown>,
  ): Promise<AdminIntegration>;
  /**
   * `POST /platform/tenants/:id/installs/:key/test` (…manage). `409 CONFLICT` while no
   * credentials are stored.
   */
  testInstall(tenantId: string, integrationKey: string): Promise<InstallProbeResult>;
  /** `GET /platform/tenants/:id/installs/:key/calls`. Redacted rows, newest first. */
  listInstallCalls(
    tenantId: string,
    integrationKey: string,
    query?: InstallCallQuery,
  ): Promise<Page<IntegrationCall>>;
  /** `GET /platform/tenants/:id/installs/:key/health`. The check history. */
  listInstallHealth(
    tenantId: string,
    integrationKey: string,
    query?: { limit?: number; cursor?: string | null },
  ): Promise<Page<IntegrationHealthCheck>>;

  /* ------------------------------------------------------------------ plans */

  /** `GET /platform/plans` (platform.plans.manage). Each plan carries every feature key. */
  listPlans(): Promise<Plan[]>;
  /** `POST /platform/plans` (platform.plans.manage). */
  createPlan(input: CreatePlanRequest): Promise<Plan>;
  /**
   * One cell of the matrix, as `PATCH /platform/plans/:id` with a one-row `features`
   * array (platform.plans.manage). Only the cells named are written.
   */
  setPlanFeature(planId: string, featureKey: FeatureKey, enabled: boolean): Promise<Plan>;

  /* ---------------------------------------------------------------- catalog */

  /** `GET /platform/integrations` (platform.catalog.manage). Includes `disabled` entries. */
  listCatalog(): Promise<CatalogEntry[]>;
  /** `POST /platform/integrations` (platform.catalog.manage). */
  createCatalogEntry(input: CreateCatalogEntry): Promise<CatalogEntry>;
  /** `PATCH /platform/integrations/:key` (platform.catalog.manage). No `key`, no `category`. */
  updateCatalogEntry(key: string, patch: UpdateCatalogEntry): Promise<CatalogEntry>;

  /* ------------------------------------------------------------------ users */

  /** `GET /platform/users` (platform.users.read). Cursor-paged. */
  listUsers(query?: PlatformUserListQuery): Promise<Page<PlatformUserSummary>>;
  /** `GET /platform/users/:id` (platform.users.read). Memberships, sessions and MFA. */
  getUser(userId: string): Promise<PlatformUserDetail>;
  /** `POST /platform/users/:id/sessions/revoke-all` (platform.users.manage, **step-up**). */
  revokeUserSessions(userId: string): Promise<PlatformUserDetail>;
  /** `POST /platform/users/:id/mfa/reset` (platform.users.manage, **step-up**, audited). */
  resetUserMfa(userId: string, input: { reason: string }): Promise<PlatformUserDetail>;
  /** `POST /platform/users/:id/active` (platform.users.manage, **step-up**). */
  setUserActive(
    userId: string,
    input: { active: boolean; reason: string },
  ): Promise<PlatformUserSummary>;
  /**
   * `POST /platform/users/:id/promote` (platform.superadmin.promote, **step-up**).
   *
   * `confirmation` is the target's own email address, typed by the operator
   * (`confirmationPhraseFor`). `platform_support` is granted at once and answers
   * `pending: null`; `platform_admin` opens a promotion a **second** administrator must
   * confirm, and comes back as `pending`.
   */
  promoteUser(
    userId: string,
    input: { role: PlatformRole; reason: string; confirmation: string },
  ): Promise<{ user: PlatformUserSummary; pending: PendingPromotion | null }>;
  /**
   * `POST /platform/users/:id/promote/confirm` (platform.superadmin.promote, **step-up**).
   * Must be somebody other than the requester; requests expire after 24 hours.
   */
  confirmPromotion(
    userId: string,
    input: { promotionId: string },
  ): Promise<{ user: PlatformUserSummary; pending: PendingPromotion | null }>;
  /**
   * `GET /platform/promotions` (platform.superadmin.promote). Cursor-paged.
   *
   * The other half of the two-person rule: `promoteUser` hands the promotion id to the
   * administrator who **asked**, and they are the one person who may not confirm it.
   * Gated on `platform.superadmin.promote` rather than `platform.users.read`, so
   * `platform_support` never reads who is about to be given the keys - which is why the
   * panel is absent for them rather than empty.
   *
   * `status` defaults to `'pending'`, which lists only requests that have not expired.
   */
  listPendingPromotions(query?: {
    status?: PromotionStatus;
    limit?: number;
    cursor?: string | null;
  }): Promise<Page<PendingPromotionRow>>;
  /** `POST /platform/users/:id/demote` (platform.superadmin.promote, **step-up**). */
  demoteUser(
    userId: string,
    input: { reason: string; confirmation: string },
  ): Promise<{ user: PlatformUserSummary; pending: PendingPromotion | null }>;
  /** `POST /platform/users` (platform.users.manage, **step-up**) - an invitation. */
  invitePlatformUser(input: InvitePlatformUserRequest): Promise<PlatformUserSummary>;

  /* ------------------------------------------------------------------- logs */

  /** `GET /platform/logs/audit` (platform.logs.read). The cross-tenant read is audited. */
  listAudit(query?: AuditQuery): Promise<Page<PlatformAuditRow>>;
  /**
   * `GET /platform/integrations/calls` (platform.logs.read, audited
   * `event.platform.cross_tenant_access`).
   */
  listIntegrationCalls(query?: IntegrationCallFilters): Promise<Page<IntegrationCall>>;
  /** `GET /platform/logs/jobs` (platform.logs.read). One row per source queue. */
  listQueues(): Promise<QueueSummary[]>;
  /** `GET /platform/logs/jobs/dead-letters` (platform.logs.read). */
  listDeadLetters(query?: { queue?: string | 'all'; limit?: number }): Promise<DeadLetterJob[]>;
  /**
   * `POST /platform/logs/jobs/dead-letters/:jobId/redrive`
   * (platform.logs.read + platform.tenant.manage, **step-up**). The reason is audited.
   */
  redriveJob(jobId: string, input: { reason: string }): Promise<DeadLetterJob[]>;
  /** `POST /platform/logs/jobs/dead-letters/:jobId/discard` (…, **step-up**). */
  discardJob(jobId: string, input: { reason: string }): Promise<DeadLetterJob[]>;
  /** `GET /platform/logs/requests` (platform.logs.read). */
  getRequestSummary(range: RequestRange): Promise<RequestSummary>;
  /** `GET /platform/logs/errors` (platform.logs.read). Grouped by surface, route, status. */
  listErrorGroups(range: RequestRange): Promise<ErrorGroup[]>;

  /* --------------------------------------------------------------- settings */

  /** `GET /platform/settings` (platform.settings.manage). */
  getSettings(): Promise<PlatformSettings>;
  /** `PATCH /platform/settings` (platform.settings.manage, **step-up**). */
  saveSettings(patch: PlatformSettingsPatch): Promise<PlatformSettings>;

  /* --------------------------------------------------------------- feedback */

  /** `GET /platform/feedback` (feedback.moderate). Cursor-paged. */
  listFeedback(query?: FeedbackQuery): Promise<Page<PlatformFeedbackItem>>;
  /** `PATCH /platform/feedback/:id` (feedback.moderate). */
  updateFeedback(itemId: string, patch: ModerateFeedbackRequest): Promise<PlatformFeedbackItem>;
  /**
   * `POST /platform/feedback/:itemId/comments` (feedback.moderate).
   *
   * Answers the **whole item**, so the board re-renders from one response rather than
   * stitching a comment onto a row it already holds. A super admin holds
   * `feedback.moderate`, so `isInternal: true` is honoured and the note stays invisible
   * to the clinic; anything else is the reply the clinic reads (rules §50.9).
   */
  commentOnFeedback(
    itemId: string,
    input: { body: string; isInternal: boolean },
  ): Promise<PlatformFeedbackItem>;

  /* ----------------------------------------------------------------- health */

  /** `GET /platform/health` (platform.health.read). Never throws server-side. */
  getPlatformHealth(): Promise<PlatformHealth>;
  /** `GET /platform/health/tenants` (platform.health.read). */
  listTenantHealth(): Promise<TenantHealthRow[]>;
  /** `GET /platform/reports/usage` (platform.health.read). Counts only, never rows. */
  getUsageReport(query?: { from?: string; to?: string; tenantId?: string }): Promise<{
    range: { from: string; to: string };
    tenants: TenantUsageRow[];
  }>;
}

/**
 * Thrown by the mock for the rules the server owns, so the UI shows the same wording it
 * would show for the API's own refusal. The real client throws `ApiError`, whose
 * `message` is the server's sentence (API.md §1.6) - including `503 MAINTENANCE`, which
 * is rendered verbatim and never replaced with a guess.
 */
export class PlatformClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PlatformClientError';
  }
}

/**
 * The permission keys the platform endpoints check, named beside the methods that call
 * them. Display only: every endpoint checks the same key on the server (rules §17.2,
 * §22.2, SECURITY.md §5.1).
 */
export const PLATFORM_ENDPOINT_PERMISSIONS = {
  /** Revoke sessions, reset MFA, deactivate, invite. `platform_admin` only. */
  usersManage: 'platform.users.manage',
  /** Promote and demote a platform role. `platform_admin` only. */
  superadminPromote: 'platform.superadmin.promote',
  /** The health card and the usage report. Both platform roles hold it. */
  healthRead: 'platform.health.read',
  /**
   * Set, replace, remove and test a tenant's integration credentials and settings
   * (M16.2, ADR-033). `platform_admin` only.
   */
  integrationsManage: 'platform.integrations.manage',
  /** Enable and disable an install for a tenant, and read its detail. */
  integrationsEnable: 'platform.integrations.enable',
  /** Feedback moderation. `clinic_admin` and `platform_admin` hold it. */
  feedbackModerate: 'feedback.moderate',
  /**
   * Redriving and discarding a dead letter. As built, both routes require
   * `platform.logs.read` **and** `platform.tenant.manage` (API.md §2.13, note 3), which
   * is exactly the pair the console gates on.
   */
  jobsManage: 'platform.tenant.manage',
  /**
   * Edit a tenant's identity (`PATCH /platform/tenants/:id`) and branding
   * (`PUT …/branding`, `POST …/files/*`). `platform_admin` only; support reads.
   */
  tenantManage: 'platform.tenant.manage',
} as const;

/** The two kinds the platform upload route takes (`PLATFORM_UPLOAD_KINDS`). */
export type PlatformUploadKind = PlatformUploadUrlRequest['kind'];

/* ------------------------------------------------------------ shared labels */

export const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  ehr: 'EHR',
  eligibility: 'Eligibility',
  payment: 'Payments',
  email: 'Email',
  sms: 'SMS',
  crm: 'CRM',
  ai_estimate: 'AI estimate',
  analytics: 'Analytics',
};

/** Catalogue display order, grouped the way the marketplace is sold (FR-MKT-01). */
export const CATEGORY_ORDER: IntegrationCategory[] = [
  'ehr',
  'eligibility',
  'payment',
  'email',
  'sms',
  'crm',
  'ai_estimate',
  'analytics',
];

/** The catalogue statuses, in the order the edit dialog offers them. */
export const CATALOG_STATUS_OPTIONS: readonly CatalogStatus[] = CATALOG_STATUSES;

/** Plain-language names for the feature keys, so the tables are readable. */
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  ai_estimate: 'AI copay estimate',
  waitlist: 'Waitlist',
  custom_domains: 'Custom domains',
  sms: 'SMS notifications',
  analytics_ping: 'Analytics ping',
  booking_v2_steps: 'Booking steps v2',
  'integration.ehr': 'EHR integrations',
  'integration.eligibility': 'Eligibility integrations',
  'integration.payment': 'Payment integrations',
  'integration.email': 'Email integrations',
  'integration.crm': 'CRM integrations',
  'integration.analytics': 'Analytics integrations',
};
